/**
 * The resort plan — drawn from real coordinates.
 *
 * WHAT CHANGED, AND WHY IT MATTERED. This used to be a hand-drawn SVG
 * reconstructed from the resort's printed map, which is an oblique aerial. The
 * file was honest that it was a reconstruction, but honesty about a distortion
 * is not the same as not having one: measured against OpenStreetMap, the
 * drawing was 353 m of shoreline rendered at an aspect of 1.26 where the ground
 * is 0.84 — stretched along the beach, squashed inland, by roughly half. Every
 * building was in the right *order* and the wrong *place*.
 *
 * That was fine while the plan was only ever a diagram. It stopped being fine
 * the moment we wanted to show a guest where they are standing, because no
 * transform reconciles those two aspect ratios: a fitted dot would be right
 * where it was calibrated and drift further wrong with every step away.
 *
 * The geometry now comes from `data/resort-plan.ts`, generated from OSM
 * footprints by `scripts/build-resort-plan.mjs`. Plan units are metres and the
 * frame is turned 8.21° so the site sits square — an angle measured from 4.7 km
 * of building wall, not eyeballed. Up is therefore *west-ish*: the Andaman Sea
 * is genuinely west, Khao Lak being on Thailand's west coast, which the old
 * header got backwards by calling it north.
 *
 * Squaring the frame is a rigid rotation and so is not the mistake the old plan
 * made. That plan was *distorted* — wrong proportions, ungeoreferenceable at any
 * angle. A rotation preserves every distance and every angle between features,
 * `lib/geo.ts` applies the identical turn, and the dot stays exact. The cost is
 * that no edge of this plan is a compass direction any more, which is why true
 * north is drawn as an arrow instead of written on an edge.
 *
 * The visual language is unchanged — same `--rp-*` tokens, same light/dark
 * behaviour. It is the same picture, in the right proportions.
 *
 * WING HIGHLIGHTS come from `venue.json`, not from a file in here. That is
 * deliberate: which block is "wing 3" is the one thing OpenStreetMap does not
 * know, so it is the one thing a person has to supply — and putting it in
 * content means it can be corrected from the AWS console the night before,
 * rather than needing a deploy. `/map?pick` emits the block to paste.
 *
 * ATTRIBUTION IS A LICENCE CONDITION. OSM data is ODbL. The credit rendered at
 * the foot of the plan is not decoration and must not be dropped.
 */
import { BOUNDARY, FEATURES, NORTH_UP_DEG, SEA, VIEW, type PlanFeature } from '../data/resort-plan';


interface Props {
  className?: string;
  /** Percentage coordinates of a venue to centre on and mark. Turns the drawing
   *  into a crop, which is what makes the 64 px thumbnail worth showing. */
  focus?: { x: number; y: number };
  label?: string;
  /** Wing label → the OSM building ids it is made of, from venue.json. */
  wings?: Record<string, number[]>;
  /** Which of those labels to pick out as "where you are staying". Silently
   *  draws nothing while `wings` has no entry for a label, which is the honest
   *  rendering of "nobody has confirmed which block that is". */
  stayWings?: string[];
}

/** Half-size of the thumbnail crop window, in metres. About 80 m of resort,
 *  which is enough to show a venue and its neighbours without becoming an
 *  abstract. */
const CROP = 82;

const path = (pts: readonly (readonly [number, number])[]) =>
  pts.map(([x, y]) => `${x},${y}`).join(' ');

/** Open ways are stroked at a real width — a 2.5 m footpath is drawn 2.5 m
 *  wide, because on a metric plan that costs nothing and is simply true. */
const STROKE: Partial<Record<PlanFeature['kind'], number>> = { walk: 2.4, road: 5 };

export default function ResortPlan({
  className = 'map-plan',
  focus,
  label = 'Resort site plan',
  wings = {},
  stayWings = [],
}: Props) {
  // A focused render is a crop, not the whole resort — otherwise the 64 px slot
  // shows the entire coastline and reads as noise.
  let viewBox = `${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`;
  if (focus) {
    const cx = VIEW.x + Math.min(VIEW.w - CROP, Math.max(CROP, (focus.x / 100) * VIEW.w));
    const cy = VIEW.y + Math.min(VIEW.h - CROP, Math.max(CROP, (focus.y / 100) * VIEW.h));
    viewBox = `${cx - CROP} ${cy - CROP} ${CROP * 2} ${CROP * 2}`;
  }

  const wanted = new Set(stayWings);
  const shown = Object.entries(wings).filter(([wing]) => wanted.has(wing));
  const stayIds = new Set(shown.flatMap(([, ids]) => ids));

  const focusX = focus ? VIEW.x + (focus.x / 100) * VIEW.w : 0;
  const focusY = focus ? VIEW.y + (focus.y / 100) * VIEW.h : 0;

  return (
    <svg
      className={className}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
    >
      {/* Layer order is load-bearing, and the obvious order is wrong.
          The base cannot be sea: only the *west* of this plan is water, and
          filling the whole box with it turns the land inland of the resort —
          the road, the neighbours — into ocean. So: neutral ground, then the
          sea cut from the real coastline (a rectangle would leave a wedge,
          because the coast runs a few degrees off the plan's edge), then the
          beach, and only then the resort's own grass over the top. The beach
          polygon overlaps the resort's western edge in OSM, so drawing it
          before the boundary is what stops it washing 150 m of lawn in sand. */}
      <rect x={VIEW.x} y={VIEW.y} width={VIEW.w} height={VIEW.h} className="rp-ground" />
      {SEA && <polygon points={path(SEA)} className="rp-sea" />}
      {FEATURES.filter((f) => f.kind === 'sand').map((f) => (
        <polygon key={f.id} points={path(f.pts)} className="rp-f-sand" />
      ))}
      <polygon points={path(BOUNDARY)} className="rp-grass" />

      {FEATURES.filter((f) => f.kind !== 'sand').map((f) => {
        const w = STROKE[f.kind];
        const cls = `rp-f-${f.kind}`;
        if (!f.closed) {
          return (
            <polyline
              key={f.id}
              points={path(f.pts)}
              className={cls}
              strokeWidth={w}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        const stay = stayIds.has(f.id);
        return (
          <polygon
            key={f.id}
            points={path(f.pts)}
            className={stay ? `${cls} is-stay` : cls}
          />
        );
      })}

      {/* One badge per WING, not per building. OSM has the accommodation mapped
          as separate parts, so wing 3 is three footprints — badging each of them
          puts three identical "3"s on the plan and makes one wing read as three.
          The badge sits at the centre of the whole group, and the outline is
          what shows which blocks belong to it.
          Nothing renders until venue.json says which buildings a wing is, which
          is the honest drawing of "not known yet". */}
      <g className="rp-wing-no">
        {shown.map(([wing, ids]) => {
          const parts = ids
            .map((id) => FEATURES.find((f) => f.id === id))
            .filter((f): f is PlanFeature => Boolean(f));
          if (parts.length === 0) return null;
          // Centre of the group's own bounding box rather than a mean of
          // vertices: a wing whose parts differ in size would otherwise pull the
          // badge towards the biggest one instead of sitting in the middle.
          const xs = parts.flatMap((f) => f.pts.map((p) => p[0]));
          const ys = parts.flatMap((f) => f.pts.map((p) => p[1]));
          const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
          const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
          return (
            <g key={wing}>
              <circle cx={cx} cy={cy} r={10} />
              <text x={cx} y={cy + 3.9} textAnchor="middle">
                {wing}
              </text>
            </g>
          );
        })}
      </g>

      <text x={VIEW.x + VIEW.w / 2} y={VIEW.y + 18} textAnchor="middle" className="rp-legend">
        Andaman Sea
      </text>

      {/* True north, as an arrow rather than a caption on an edge.
          The frame is turned ~8° to square the buildings up, so no edge of this
          plan is a compass direction — and captioning one anyway is precisely
          the error the old plan made when it called the seaward edge "north"
          on a west-facing coast. An arrow can be 8° off and still be right;
          a word cannot. Hidden on the cropped thumbnail, where it would be
          bigger than the building it sits next to. */}
      {!focus && (
        <g
          className="rp-north"
          transform={`translate(${VIEW.x + VIEW.w - 26} ${VIEW.y + VIEW.h - 30}) rotate(${NORTH_UP_DEG})`}
        >
          <line x1="0" y1="11" x2="0" y2="-11" />
          <polygon points="0,-15 4,-6 -4,-6" />
          <text x="0" y="-19" textAnchor="middle" transform={`rotate(${-NORTH_UP_DEG} 0 -19)`}>
            N
          </text>
        </g>
      )}

      {/* ODbL requires this. It is small, and it is not optional. */}
      <text x={VIEW.x + VIEW.w - 4} y={VIEW.y + VIEW.h - 4} textAnchor="end" className="rp-credit">
        © OpenStreetMap contributors
      </text>

      {focus && (
        <>
          <circle cx={focusX} cy={focusY} r={22} className="rp-focus-halo" />
          <circle cx={focusX} cy={focusY} r={9} className="rp-focus-dot" />
        </>
      )}
    </svg>
  );
}
